// The city lights at 500 m (src/data/nightImagerySource.js: NASA's Black
// Marble 2016 with the moonlit land taken out) streamed through the
// body-generic imagery streamer (planetimagery.js) as one R8 atlas, and the
// night lights both of the Earth's surfaces draw from it: the app's globe
// (src/shaders/earth.js) and rocketry's terrain patches (terrain.js), so the
// two agree at the patch rim.
//
// The light at a point (earthNightLights) is, from far to near:
//   - the 8K earth_night.jpg as the globe has always drawn it, while one of
//     its texels is smaller than a pixel (the viewer, and orbit from far off)
//   - read through a cubic B-spline once a texel spans more than a pixel: the
//     bilinear tent of a lone bright texel printed as a soft square, which is
//     what the city lights looked like from low orbit
//   - the 500 m pages over it where they are resident and finer than the 8K,
//     scaled by NIGHT_IMAGERY_GAIN so the area-average stays the 8K's, and
//     coloured by brightness as the 8K is (deep sodium in the fringes, warm
//     white in the cores)
//   - and under 500 m, structure the data cannot resolve (nightDetail): a
//     city's streets as orbit photographs show them, motorways, arterials and
//     each neighbourhood's own street pattern, parks and lots, every layer of
//     mean 1 so it changes no average, each line blurred analytically to the
//     pixel's footprint (never thinner than a pixel, no shimmer) and handed
//     to that mean before it comes under about three pixels
//   - with an exposure by the camera's height (nightExposureAt), 1 from
//     orbit, so a city filling the view is not overexposed paper.
// Close in, light comes only from ground the data lights: the data's floor
// (NIGHT_FLOOR) takes out the sensor's halo round towns, the encoding's
// noise and the 8K's moonlit land (nightLightsFrom), and the structure only
// moves the data's light about inside what it lights.
// It also returns a coarse read of the light (about 4 km wide) for the glow
// round bright cores and for the light clouds pick up from below.
import * as THREE from 'three';
import { createPlanetImagery } from './planetimagery.js';
import { NIGHT_FLOOR, NIGHT_IMAGERY_CACHE, NIGHT_IMAGERY_DATASET, NIGHT_IMAGERY_GAIN, NIGHT_IMAGERY_GRID, NIGHT_IMAGERY_OFFSET, nightImageryUrl, nightTileAvailable } from '../data/nightImagerySource.js';
import { earthCameraFrame } from '../earthclose.js';
import { earthImageryUniforms } from './earthimagery.js';
import { coastUniforms } from './coast.js';
import { RUNNING_ON_MOBILE } from '../mobiledevice.js';

const EARTH_R = 6371000;
// Above this an 8K texel (4.9 km) is under a pixel across the view's middle
// and the pages would not be shown, so none are requested.
const STREAM_ALTITUDE = 3.0e6;
// Leaves in the selection, and an atlas three times that (R8, 26 MB with its
// mips), as the cloud pages do; a phone 20 into 36 (12 MB).
const BUDGET = RUNNING_ON_MOBILE ? 20 : 40;
const ATLAS_SIDE = RUNNING_ON_MOBILE ? 6 : 10;
const PREFETCH = [1.3, 600e3, 1500e3];

const night = createPlanetImagery({
  name: 'night', radius: EARTH_R, ...NIGHT_IMAGERY_GRID, atlasSide: ATLAS_SIDE,
  linear: true, channels: 1, bspline: true,
  cacheName: NIGHT_IMAGERY_CACHE, localUrl: nightImageryUrl, url: nightImageryUrl,
  // The set leaves out tiles without lights (nightImagerySource.js), which
  // are its offset all over: the streamer draws them dark at the level the
  // view asks for, where they had fallen back to the 8K's moonlit land.
  available: nightTileAvailable, emptyTexel: NIGHT_IMAGERY_OFFSET
});

// A rebuilt tile set is a new cache; an older build's tiles are dropped.
try {
  globalThis.caches?.keys().then((names) => {
    for (const n of names) if (n.startsWith(`night-${NIGHT_IMAGERY_DATASET}-`) && n !== NIGHT_IMAGERY_CACHE) caches.delete(n);
  }).catch(() => {});
} catch { /* no Cache Storage here */ }

// The globe skips its close-in lights inside the cap the opaque part of an
// Earth patch covers (xyz the patch centre in Earth's surface-fixed frame, w
// the cosine of the cap's radius; w over 1 is no cap), since the patch draws
// them there again. Set each frame by createGlobalNightImagery.
night.uniforms.uNightSkipCap = { value: new THREE.Vector4(0, 1, 0, 2) };
// 1 draws every light as it was before the data's floor (the globe, the
// patches, the cloud shell and the volume's glow), for before and after
// captures: window.__rkNightLampsOff = true, read each frame by the viewer's
// and rocketry's updates.
night.uniforms.uNightLegacy = { value: 0 };
// The lights' close-in exposure, from the camera's height (nightExposureAt),
// set each frame by the viewer's and rocketry's updates.
night.uniforms.uNightExposure = { value: 1 };
export const nightImageryUniforms = night.uniforms;

// A city that fills the view is a different exposure from one that is a few
// lit pixels on the night side. The globe from afar is approved at the
// data's light, and a metro filling the frame at that light read as
// overexposed paper (beige, its lines clipped white), where the ISS crew's
// photographs of a city (ISS045-E-32242, London at 400 mm) are exposed for
// it: dark between the streets, the streets orange. So the lights take an
// exposure by the camera's height, 1 from 600 km up (orbit and the globe
// are unchanged) to NIGHT_EXPOSURE[0] by 60 km, through the log of the
// height. It is one value a frame, so it is not a pattern on the screen.
const NIGHT_EXPOSURE = [0.5, 60e3, 600e3];
export function nightExposureAt(altitude) {
  if (globalThis.__rkNightLampsOff) return 1;
  const h = Math.log(Math.max(altitude, 1));
  const t = THREE.MathUtils.smoothstep(h, Math.log(NIGHT_EXPOSURE[1]), Math.log(NIGHT_EXPOSURE[2]));
  return NIGHT_EXPOSURE[0] + (1 - NIGHT_EXPOSURE[0]) * t;
}
export const getNightImageryStream = night.getStream;

const f = (v) => v.toFixed(8);
// Metres one texel of a page-level tile spans (north-south), the finest
// level's, and that level's texels round the world.
const PAGE_TEXEL_M = night.PAGE_SPAN * Math.PI / 180 / night.TILE_SIZE * EARTH_R;
const FINE = 2 ** (NIGHT_IMAGERY_GRID.maxLevel - NIGHT_IMAGERY_GRID.pageLevel);
const FINE_TEXEL_M = PAGE_TEXEL_M / FINE;
const FINE_TEXELS = { w: night.PAGE_WIDTH * night.TILE_SIZE * FINE, h: night.PAGE_HEIGHT * night.TILE_SIZE * FINE };
// Finest texels per page, for the coarse read's footprint.
const FINE_PER_PAGE = night.TILE_SIZE * FINE;
// nightDetail's sub-cells (four to a finest texel) per degree of longitude
// and latitude, and the cell at lon 0, lat 0: q = Q_ZERO + geo * Q_PER_DEG,
// as nightLightsFrom builds q from uv.
const NIGHT_SUB = 4;
const Q_PER_DEG = [FINE_TEXELS.w * NIGHT_SUB / 360, -FINE_TEXELS.h * NIGHT_SUB / 180];
const Q_ZERO = [FINE_TEXELS.w * NIGHT_SUB / 2, FINE_TEXELS.h * NIGHT_SUB / 2];

// Needs nothing before it. Call earthNightLights in uniform control flow
// (it takes screen derivatives).
export const NIGHT_IMAGERY_GLSL = /* glsl */`
${night.glsl}
const float NIGHT_R = ${f(EARTH_R)};
const float NIGHT_PAGE_TEXEL_M = ${f(PAGE_TEXEL_M)};
const float NIGHT_FINE_TEXEL_M = ${f(FINE_TEXEL_M)};
const float NIGHT_GAIN = ${f(NIGHT_IMAGERY_GAIN)};
const float NIGHT_OFFSET = ${NIGHT_IMAGERY_OFFSET.toFixed(1)};
// Sub-cells per finest-level texel for the detail, and that grid's size.
const float NIGHT_SUB = ${NIGHT_SUB.toFixed(1)};
uniform vec4 uNightSkipCap;
uniform float uNightLegacy;
// The close-in exposure (nightExposureAt), 1 from orbit.
uniform float uNightExposure;
// The pages' shoulder (their units, linear).
const float NIGHT_SHOULDER = 0.6;
const vec2 NIGHT_FINE = vec2(${f(FINE_TEXELS.w)}, ${f(FINE_TEXELS.h)});
const vec2 NIGHT_FLOOR = vec2(${f(NIGHT_FLOOR[0])}, ${f(NIGHT_FLOOR[1])});

struct NightLights {
  vec3 light;   // linear radiance in the 8K's units (the caller's gains apply)
  vec3 coarse;  // the same about 4 km wide, for glow and lit cloud bases
  float close;  // 0 where the 8K texel is under a pixel, 1 once it spans 1.5
  float page;   // how much of light came from the 500 m pages
  float level;  // the data's light before the structure (linear, shouldered)
  float display; // the data's display value (0..1), as nightDetail reads it
  float floor;  // the data's floor there (1 from afar), already in light
  float haze;   // how much of the glow round bright cores to draw (1 from afar)
};

float nightLuma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
float nightSrgbToLinear(float v) { return v <= 0.04045 ? v / 12.92 : pow((v + 0.055) / 1.055, 2.4); }
// A light in the 8K's linear units as the pages' display value: the gain
// takes the 8K's mean back to the pages', then their sRGB encoding.
float nightPageDisplay(float level) {
  float x = max(level, 0.0) / NIGHT_GAIN;
  return x <= 0.0031308 ? x * 12.92 : 1.055 * pow(x, 1.0 / 2.4) - 0.055;
}
// The data's floor on a display value (NIGHT_FLOOR, nightImagerySource.js).
float nightFloor(float display) { return smoothstep(NIGHT_FLOOR.x, NIGHT_FLOOR.y, display); }
// The 8K's own lights, its moonlit land (under the floor in the pages'
// units) taken out.
vec3 nightLightsOnly(vec3 c) { return c * nightFloor(nightPageDisplay(nightLuma(c))); }

// The 8K through a cubic B-spline (four bilinear taps), mixed in from the
// plain bilinear read as a texel grows past a pixel, so a lone bright texel
// is a soft round light and the far view is untouched.
vec3 nightMapRead(sampler2D map, vec2 uv, vec2 texel, float magnify) {
  vec3 bilinear = texture2D(map, uv).rgb;
  float k = smoothstep(1.0, 2.0, magnify);
  if (k <= 0.0) return bilinear;
  vec2 st = uv / texel - 0.5, i = floor(st), t = st - i;
  vec2 w0 = (1.0 - t) * (1.0 - t) * (1.0 - t) / 6.0, w1 = (4.0 - 6.0 * t * t + 3.0 * t * t * t) / 6.0;
  vec2 w2 = (1.0 + 3.0 * t + 3.0 * t * t - 3.0 * t * t * t) / 6.0, w3 = t * t * t / 6.0;
  vec2 g0 = w0 + w1, g1 = w2 + w3;
  vec2 h0 = (i - 0.5 + w1 / g0) * texel, h1 = (i + 1.5 + w3 / g1) * texel;
  vec3 spline = g0.y * (g0.x * textureLod(map, h0, 0.0).rgb + g1.x * textureLod(map, vec2(h1.x, h0.y), 0.0).rgb) +
    g1.y * (g0.x * textureLod(map, vec2(h0.x, h1.y), 0.0).rgb + g1.x * textureLod(map, h1, 0.0).rgb);
  return mix(bilinear, spline, k);
}

uint nightPcg(uint v) {
  uint s = v * 747796405u + 2891336453u;
  uint w = ((s >> ((s >> 28u) + 4u)) ^ s) * 277803737u;
  return (w >> 22u) ^ w;
}
float nightRand(ivec2 c, uint seed) {
  return float(nightPcg(uint(c.x) * 1597334677u ^ uint(c.y) * 3812015801u ^ seed)) / 4294967295.0;
}

// Value noise on q's integer lattice, 0..1.
float nightValueNoise(vec2 q, uint seed) {
  vec2 i = floor(q), f = q - i, u = f * f * (3.0 - 2.0 * f);
  ivec2 c = ivec2(i);
  float a = nightRand(c, seed), b = nightRand(c + ivec2(1, 0), seed);
  float d = nightRand(c + ivec2(0, 1), seed), e = nightRand(c + ivec2(1, 1), seed);
  return mix(mix(a, b, u.x), mix(d, e, u.x), u.y);
}

// A uniform 0..1 from a seed and a key.
float nightU(uint s, uint k) { return float(nightPcg(s ^ (k * 2654435769u))) / 4294967295.0; }
uint nightCellSeed(ivec2 c, uint seed) { return nightPcg(uint(c.x) * 1597334677u ^ uint(c.y) * 3812015801u ^ seed); }
// A turn by a radians.
mat2 nightRot(float a) { float c = cos(a), s = sin(a); return mat2(c, s, -s, c); }

// A lit line of unit integral across it: a gaussian of sigma s0 (q's units),
// widened to the pixel's footprint (foot, the same units). The convolution
// of two gaussians, so its light, and the mean of any pattern of them, does
// not change with distance, and a line is never thinner than a pixel.
float nightLineN(float e, float s0, float foot) {
  float s2 = s0 * s0 + 0.1764 * foot * foot;
  return exp(-0.5 * e * e / s2) * inversesqrt(6.2831853 * s2);
}

// Lamps every b along a lit line at distance t along it, standing at
// b (k + phase): a mean-1 modulation, handed to its mean once they come
// under about three pixels apart.
float nightBeads(float t, float b, float phase, float foot) {
  float k = 1.0 - smoothstep(0.25 * b, 0.5 * b, foot);
  if (k <= 0.0) return 1.0;
  float x = t / b - phase;
  return mix(1.0, b * nightLineN(b * (x - floor(x + 0.5)), 0.02, foot), k);
}

// The cell of the nearest of jittered sites on p's unit lattice (metric
// scales p to the ground) and its site, the second nearest's (cell2, site2)
// and (e) the distance to the bisector between them, for the hand-over
// between regions.
ivec2 nightNearest(vec2 p, vec2 metric, float jitter, uint seed, out vec2 site, out float e, out ivec2 cell2, out vec2 site2) {
  vec2 cell = floor(p);
  float d1 = 1e9, d2 = 1e9;
  vec2 p1 = vec2(0.0), p2 = vec2(0.0);
  ivec2 c1 = ivec2(0), c2 = ivec2(0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    ivec2 c = ivec2(cell) + ivec2(i, j);
    vec2 s = vec2(c) + 0.5 + (vec2(nightRand(c, seed), nightRand(c, seed + 1u)) - 0.5) * jitter;
    vec2 d = (p - s) * metric;
    float dd = dot(d, d);
    if (dd < d1) { d2 = d1; p2 = p1; c2 = c1; d1 = dd; p1 = s; c1 = c; }
    else if (dd < d2) { d2 = dd; p2 = s; c2 = c; }
  }
  site = p1; site2 = p2; cell2 = c2;
  e = abs(dot((p - 0.5 * (p1 + p2)) * metric, normalize((p2 - p1) * metric)));
  return c1;
}

// One family of long roads in lp's frame: lines across the unit direction
// d, one about every s (each shifted by up to half of jitter s), bent by a
// warp of up to warp s over about 3 s, lit in runs about run s long (each on
// with chance onShare, at its own brightness, LED with chance ledP),
// gaussian of s0 across, widened to the pixel, lamps every b along them. x:
// the light, mean 1 over a few s; y: its LED part.
// One hash a line (its shift, its runs' phase) and one a run (on,
// brightness, LED, the lamps' phase), split into bytes: this runs for four
// families in every lit pixel close in.
float nightByte(uint h, uint i) { return float((h >> (8u * i)) & 255u) / 256.0; }
vec2 nightRoads(vec2 lp, vec2 d, float s, float jitter, float warp, float run, float onShare, float ledP, float s0, float b, float foot, uint seed) {
  float u = dot(lp, d), t = dot(lp, vec2(-d.y, d.x));
  if (warp > 0.0) u += warp * s * 2.0 * (nightValueNoise(lp / (3.0 * s), seed) - 0.5);
  float x = u / s, k0 = floor(x);
  vec2 acc = vec2(0.0);
  // Lines move at most a quarter of s, so only the two either side matter.
  for (int dk = 0; dk <= 1; dk++) {
    int k = int(k0) + dk;
    uint hl = nightPcg(uint(k) * 2654435761u ^ seed);
    float off = (nightByte(hl, 0u) - 0.5) * jitter;
    uint hr = nightPcg(hl ^ uint(int(floor(t / (run * s) + nightByte(hl, 1u)))) * 2246822519u);
    float on = step(nightByte(hr, 0u), onShare);
    float lvl = 0.6 + 0.8 * nightByte(hr, 1u);
    float v = on * lvl * nightLineN((x - float(k) - off) * s, s0, foot);
    if (v > 0.0) v *= nightBeads(t, b, nightByte(hr, 3u), foot);
    acc += vec2(v, v * step(1.0 - ledP, nightByte(hr, 2u)));
  }
  return acc * (s / onShare);
}

// The share of a neighbourhood's ground that is LED white at lp: towns are
// converted a few kilometres at a time, so this is a soft field about 6 km
// across cut at the share p.
float nightLedZone(vec2 lp, float p, uint seed) {
  float v = 0.7 * nightValueNoise(lp / 60.0, seed) + 0.3 * nightValueNoise(lp / 26.0, seed + 1u);
  float t = 0.5 + (0.5 - clamp(p, 0.0, 1.0)) * 0.5;
  return smoothstep(t - 0.04, t + 0.04, v);
}

// One region's towns at q (see nightDetail): region its cell, site its site
// in q. x: the light, mean 1; y: its LED share.
vec2 nightCity(vec2 q, ivec2 region, vec2 site, float cosLat, float foot, float f, float core, float ledShare, float kA) {
  uint rs = nightCellSeed(region, 0x5bd1e995u);
  // Planned grids are the towns of the Americas (the US and Canadian land
  // surveys' mile grid, the Spanish colonial grid) and Australia's; old
  // irregular towns most everywhere else.
  vec2 sg = site / (NIGHT_FINE * NIGHT_SUB);
  float sLon = sg.x * 360.0 - 180.0, sLat = 90.0 - sg.y * 180.0;
  float pPlanned = 0.08 + 0.52 * step(-170.0, sLon) * step(sLon, -34.0) + 0.37 * step(112.0, sLon) * step(sLon, 155.0) * step(sLat, -10.0);
  float planned = step(1.0 - pPlanned, nightU(rs, 1u));
  float rB = nightU(rs, 2u);
  float theta = planned > 0.5 ? (rB < 0.5 ? 0.0 : (rB - 0.75) * 1.4) : rB * 3.14159265;
  // The region's frame: q's cells from the site, in metres (east to west
  // they narrow with the latitude), turned.
  vec2 lp = nightRot(theta) * ((q - site) * vec2(max(cosLat, 0.05), 1.0));
  // Where towns are LED white: a lean toward it in the metros' cores, and in
  // East Asia, whose cities were lit white (mercury, fluorescent, then LED)
  // where the West's were sodium.
  float eastAsia = step(100.0, sLon) * step(sLon, 146.0) * step(18.0, sLat) * step(sLat, 46.0);
  float ledP = clamp(0.5 * ledShare + 0.25 * core + 0.6 * eastAsia, 0.0, 0.85);

  // Motorways: long curves about 8 km apart, lit in runs.
  float aH = nightU(rs, 10u) * 3.14159265;
  vec2 H = nightRoads(lp, vec2(cos(aH), sin(aH)), 66.0, 0.5, 0.6, 5.0, 0.6, clamp(ledP + 0.15, 0.0, 0.7), 0.2, 0.3, foot, rs + 20u);

  // Arterials: in an old town three families of bent roads about 1.2 to
  // 2.5 km apart at their own angles (together a road every 600 m or so), lit in runs of a few kilometres, so the
  // network is irregular cells of crossing roads that end at junctions; on a
  // planned grid the grid's own lines, the mile or the half mile, every one
  // lit.
  vec2 A = vec2(0.0);
  float ca = planned > 0.5 ? (nightU(rs, 4u) < 0.75 ? 13.0 : 6.5) : mix(12.0, 16.0, nightU(rs, 4u));
  if (planned > 0.5) {
    A += nightRoads(lp, vec2(1.0, 0.0), ca, 0.0, 0.0, 40.0, 1.0, ledP, 0.12, 0.28, foot, rs + 60u);
    A += nightRoads(lp, vec2(0.0, 1.0), ca, 0.0, 0.0, 40.0, 1.0, ledP, 0.12, 0.28, foot, rs + 76u);
    A *= 0.5;
  } else {
    // One warp of the ground bends all three, over about three cells and
    // again over one.
    vec2 w1 = lp / (3.0 * ca), w2 = lp / (1.1 * ca);
    vec2 la = lp + (vec2(nightValueNoise(w1, rs + 33u), nightValueNoise(w1 + 5.3, rs + 35u)) - 0.5) * (1.3 * ca)
      + (vec2(nightValueNoise(w2, rs + 37u), nightValueNoise(w2 + 2.9, rs + 39u)) - 0.5) * (0.35 * ca);
    for (int i = 0; i < 3; i++) {
      float a = float(i) * 1.0471976 + (nightU(rs, 30u + uint(i)) - 0.5) * 0.5;
      float s = ca * (0.8 + 0.5 * nightU(rs, 40u + uint(i)));
      A += nightRoads(la, vec2(cos(a), sin(a)), s, 0.5, 0.0, 3.5, 0.8, ledP, 0.12, 0.28, foot, rs + 60u + uint(i) * 16u);
    }
    A *= 1.0 / 3.0;
  }

  // Neighbourhoods about 500 m across, each with its own streets, LED or
  // sodium, density; some are parks (dark but for the roads through and
  // round them) and a few floodlit lots, ports or yards (lit wall to wall).
  // In an old town they are found in a warped lattice, so their edges are
  // not straight; on a planned grid they are its half squares.
  vec2 centre;
  ivec2 pc;
  if (planned > 0.5) {
    pc = ivec2(floor(lp / (ca * 0.5)));
    centre = (vec2(pc) + 0.5) * ca * 0.5;
  } else {
    vec2 pp = lp / 4.2;
    pp += (vec2(nightValueNoise(pp * 0.5, rs + 81u), nightValueNoise(pp * 0.5 + 4.4, rs + 83u)) - 0.5) * 0.7;
    vec2 pSite, pSite2; float pE; ivec2 pc2;
    pc = nightNearest(pp, vec2(1.0), 0.85, rs + 85u, pSite, pE, pc2, pSite2);
    centre = pSite * 4.2;
  }
  uint cs = nightCellSeed(pc, rs);
  float pPark = mix(0.3, 0.08, f) * (1.0 - 0.5 * core) * (1.0 - 0.4 * planned);
  float pLot = mix(0.003, 0.008, f) + 0.012 * core;
  float lot = 1.0 - smoothstep(pLot - 0.005, pLot + 0.005, nightU(cs, 2u));
  float lived = smoothstep(pPark - 0.04, pPark + 0.04, nightU(cs, 1u)) * (0.75 + 0.5 * nightU(cs, 3u));
  const float LOT = 1.6;
  float m = mix(lived, LOT, lot) / ((1.0 - pLot) * (1.0 - pPark) + pLot * LOT);
  // The parks and lots are kept while a neighbourhood is three pixels
  // across, as the arterials are.
  float kP = 1.0 - smoothstep(1.0, 2.0, foot);
  float cellM = mix(1.0, m, kP);
  // A neighbourhood is LED or sodium whole, by the zone at its centre, so a
  // converted town is a run of whole neighbourhoods.
  float ledR = mix(step(0.5, nightLedZone(centre, ledP, rs + 3u)), 0.45, lot);

  // The neighbourhood's streets: two families of lines. On a planned grid
  // most are the city's own blocks (100 by 200 m) in its frame, so the grid
  // runs on from one to the next and the arterials fall on block lines, and
  // the rest (more of them in the fringes) are the curving streets of a
  // suburb laid inside the grid's squares. In an old town every one is at
  // its own angle and spacing (80 to 270 m), gently bent.
  float R = 1.0;
  if (foot < 1.45) {
    bool grid = planned > 0.5 && nightU(cs, 10u) >= mix(0.55, 0.2, f);
    vec2 rl, sp;
    if (grid) {
      rl = lp;
      sp = vec2(1.625, 0.8125);
      if (nightU(rs, 5u) < 0.5) sp = sp.yx;
    } else {
      rl = lp - centre;
      float bend = planned > 0.5 ? 0.9 : 0.45;
      rl += (vec2(nightValueNoise(rl / 2.5, cs + 71u), nightValueNoise(rl / 2.5 + 5.1, cs + 73u)) - 0.5) * bend;
      rl = nightRot(nightU(cs, 5u) * 3.14159265) * rl;
      sp.x = 0.65 + 0.5 * nightU(cs, 6u);
      sp.y = sp.x * (1.5 + 1.5 * nightU(cs, 7u));
      rl += vec2(nightU(cs, 8u), nightU(cs, 9u)) * sp;
    }
    float fam = 0.0;
    for (int ax = 0; ax < 2; ax++) {
      float s = sp[ax], so = sp[1 - ax], u = ax == 0 ? rl.x : rl.y, t = ax == 0 ? rl.y : rl.x;
      float k = 1.0 - smoothstep(0.3 * s, 0.65 * s, foot);
      if (k <= 0.0) { fam += 1.0; continue; }
      float kk = floor(u / s + 0.5);
      // Each street is lit in runs of two to four blocks, a quarter of them
      // dark, so the mesh is streets meeting at corners and ending, not a
      // woven hatch; on a planned grid they run on for many blocks.
      uint ls = nightPcg((grid ? rs : cs) ^ uint(int(kk) * 2 + ax) * 668265263u);
      float runL = so * (grid ? 8.0 : 2.0 + 2.0 * nightByte(ls, 0u));
      float onS = grid ? 0.88 : 0.75;
      uint rn = nightPcg(ls ^ uint(int(floor(t / runL + nightByte(ls, 1u)))) * 374761393u);
      float on = step(nightByte(rn, 0u), onS);
      float lvl = 0.55 + 0.9 * nightByte(rn, 1u);
      float v = on * lvl * nightLineN(u - kk * s, 0.055, foot) * s / onS;
      v *= nightBeads(t, 0.32, nightByte(ls, 2u), foot);
      fam += mix(1.0, v, k);
    }
    // A floodlit lot is lit wall to wall.
    R = mix(0.5 * fam, 1.0, lot);
  }
  // Density over a few blocks to a couple of kilometres, soft, mean 1.
  float kFine = 1.0 - smoothstep(0.8, 1.6, foot);
  float nM = 0.6 * nightValueNoise(lp / 14.0, rs + 61u) + 0.4 * (kFine > 0.0 ? mix(0.5, nightValueNoise(lp / 4.5, rs + 67u), kFine) : 0.5);
  // A lognormal of the field (sd 0.156 with both octaves, each 0.216), its
  // spread held as the fine octave goes so the mean stays 1.
  float M = exp(0.4 * (nM - 0.5) / (0.216 * sqrt(0.36 + 0.16 * kFine * kFine)) - 0.08);

  // Each layer to its mean as the view draws back, then the shares of the
  // light: the dimmer the data, the more of it is on the main roads.
  vec2 hA = mix(vec2(1.0, ledShare), vec2(H.x, H.y / max(H.x, 1e-5)), kA);
  vec2 aA = mix(vec2(1.0, ledShare), vec2(A.x, A.y / max(A.x, 1e-5)), kA);
  M = mix(1.0, M, kA);
  ledR = mix(ledShare, ledR, kA);
  float wH = mix(0.09, 0.04, f), wA = mix(0.42, 0.31, f), wF = mix(0.03, 0.04, f);
  float wR = 1.0 - wH - wA - wF;
  float hv = wH * hA.x, av = wA * aA.x, nb = cellM * M * (wR * R + wF);
  float S = hv + av + nb;
  return vec2(S, (hv * hA.y + av * aA.y + nb * ledR) / max(S, 1e-5));
}

// Structure under the data at q (sub-cells of the finest level's grid, four
// to a 488 m texel, about 122 m), for a pixel of foot sub-cells: a light that
// averages 1 over a few texels and the share of it that is LED white. It is
// laid out the way cities are lit from orbit (ISS045-E-32242, London from
// 400 km at 400 mm; ISS006-E-36913, Los Angeles; ISS037-E-2604, the Bay
// Area): a web of streets that is each town's own, long arterials and
// motorways through it, dark parks between, and between the streets nothing
// but the faint light of windows and yards.
//   Regions about 36 km across, each in its own frame (turned, and in metres:
//     q's cells narrow east to west with the latitude): planned on a grid
//     (the mile or half-mile grid, square to north in half of them; most
//     regions in the Americas and Australia) or old and irregular. Two
//     regions cross-fade over a kilometre either side of their boundary.
//   Motorways: long curves about 8 km apart, lit in runs.
//   Arterials: three families of gently bent long roads at their own angles
//     in an old town, the grid's lines on a planned one.
//   Neighbourhoods about 500 m across (nightCity), each with its own street
//     pattern (on a planned grid most of them the grid's blocks, the rest a
//     suburb's curving streets), LED or sodium whole, and density; some are
//     parks, a few floodlit lots.
//   Lamps along every line, under about 15 km.
// Each layer averages 1 and is handed to that mean before its lines come
// under about three pixels apart, so none of it reads as grain, and by a
// pixel of 430 m (about 500 km up) all of it is gone: there the data is the
// picture. The dimmer the data the more of its light is on the main roads and
// the more neighbourhoods are dark, so a town thins out along its roads;
// where the wide read is brightest, in the metros' cores, the streets turn
// white and the lots come more often.
// brightness is the data's display value, core 0..1 the metro's core and
// cosLat the latitude's cosine.
vec2 nightDetail(vec2 q, float foot, float brightness, float core, float ledShare, float cosLat) {
  float kA = 1.0 - smoothstep(1.5, 3.5, foot);
  if (kA <= 0.0) return vec2(1.0, ledShare);
  float f = smoothstep(0.25, 0.95, brightness);
  // The two nearest regions, cross-faded within 8 cells (1 km) of their
  // boundary; elsewhere one is drawn.
  vec2 metric = vec2(max(cosLat, 0.05), 1.0);
  vec2 rSite, rSite2; float rE; ivec2 rc2;
  ivec2 rc = nightNearest(q / 300.0, metric, 0.8, 901u, rSite, rE, rc2, rSite2);
  vec2 S = nightCity(q, rc, rSite * 300.0, cosLat, foot, f, core, ledShare, kA);
  float w2 = 0.5 * (1.0 - smoothstep(0.0, 8.0, rE * 300.0));
  if (w2 > 0.0) S = mix(S, nightCity(q, rc2, rSite2 * 300.0, cosLat, foot, f, core, ledShare, kA), w2);
  return S;
}

// The colour of a light of radiance L (the data's units, times its
// structure), luminance 1: sodium orange in the dim and middle lights,
// warming to a hot white only in the brightest, as the ISS photographs show
// it (ISS041-E-74232); LED white for the share led.
vec3 nightLightTint(float L, float led) {
  vec3 sodium = mix(vec3(1.0, 0.45, 0.12) / 0.5431, vec3(1.0, 0.78, 0.50) / 0.8066, smoothstep(0.5, 1.8, L));
  return mix(sodium, vec3(0.86, 0.95, 1.0) / 0.9345, led);
}

// The globe's slow regional noise (earth.js cityRegion, the same hash and
// lattice), as the share of the region's lights that are LED white, so the
// globe and the patches tint a city alike.
float nightHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float nightLedShare(vec2 uv) {
  vec2 p = uv * 14.0 + vec2(7.3, 11.1);
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  float v = mix(mix(nightHash(i), nightHash(i + vec2(1.0, 0.0)), u.x),
    mix(nightHash(i + vec2(0.0, 1.0)), nightHash(i + vec2(1.0, 1.0)), u.x), u.y);
  return smoothstep(0.40, 0.70, v);
}

// The sea round a point for the lights, from a water coverage (the ocean
// texture's R, 1 sea, 0 land): x the coverage sharpened to its own coastline
// where a texel spans many pixels (as earth.js sharpens the sea), y how open
// the sea is (1 with no land within about 25 km, read a few levels up the
// mip chain). cover is the plain read, open its read at that level. Takes
// screen derivatives: call in uniform control flow.
vec2 nightWater(float cover, float open) {
  float edge = clamp(fwidth(cover), 0.02, 0.5);
  return vec2(smoothstep(0.5 - edge, 0.5 + edge, cover), smoothstep(0.97, 1.0, open));
}

// The ground metres of the pixel's two screen axes (x, y) at uv.
vec2 nightPixelAxes(vec2 uvDx, vec2 uvDy, float lat) {
  vec2 m = vec2(6.2831853 * NIGHT_R * max(cos(lat), 0.02), 3.14159265 * NIGHT_R);
  return vec2(length(uvDx * m), length(uvDy * m));
}

// The lights from the 8K's reads (map8 as drawn, coarse8 its plain bilinear
// read; both zero where there is no 8K, on the patches) with the 500 m pages
// and the detail over them. texelM is the 8K's texel in metres, which sets
// where "close" begins whether or not the 8K itself is read. water is
// nightWater's: no roads, streets or lamps on the sea, and close
// in the light the data spills off the shore (its 500 m texels and the 8K's
// 4.9 km ones straddle the coast) goes dark there too, so the coast reads
// sharp; out on open sea the data's own lights (ships, platforms) stay, as
// they are, without structure. Takes screen derivatives: call in uniform
// control flow.
//
// Close in, light comes only from ground the data lights. The floor
// (nightFloor) takes out everything under it, the 8K's moonlit land with it:
// the 8K is the Black Marble with its land, a navy that reads as light, and
// the structure cut it into towns over the whole Sahara and Greenland
// wherever no page was resident. A resident page of any level has no land in
// it, so where one is resident the 8K never has more light than it. With no
// page the 8K is a smear kilometres wider than the towns once its texel spans
// many pixels, so it goes dark there until the pages land. All of this is
// weighted by the pixel's footprint across its short axis, on under 1.5 km
// and gone by 3 km, so the globe from afar (a pixel of 4 km and more across
// the whole disc once it fits the screen, on a high-DPI screen too) is the
// picture it was, while a view grazing toward the horizon from orbit, its
// pixels kilometres long but a kilometre across, is floored.
NightLights nightLightsFrom(vec3 map8, vec3 coarse8, float wide8, float texelM, vec2 uv, float ledShare, bool skip, vec2 water) {
  NightLights n;
  vec2 uvDx = dFdx(uv), uvDy = dFdy(uv);
  vec2 duv = max(abs(uvDx), abs(uvDy));
  float lat = (uv.y - 0.5) * 3.14159265;
  float pixM = max(duv.x * 6.2831853 * NIGHT_R * max(cos(lat), 0.02), duv.y * 3.14159265 * NIGHT_R);
  vec2 axes = nightPixelAxes(uvDx, uvDy, lat);
  float pixShort = min(axes.x, axes.y);
  vec2 raw = vec2(uv.x * ${f(night.PAGE_WIDTH)}, (1.0 - uv.y) * ${f(night.PAGE_HEIGHT)});
  vec2 pageDx = dFdx(raw), pageDy = dFdy(raw);
  vec2 q = vec2(uv.x * NIGHT_FINE.x, (1.0 - uv.y) * NIGHT_FINE.y) * NIGHT_SUB;
  // The pixel in q's cells. They are a share of a degree, so east to west
  // they narrow with the latitude (to a third at 72 degrees); measured in
  // metres the structure stayed on below a pixel there and aliased into
  // single-pixel specks over Greenland.
  float foot = uNightLegacy > 0.5 ? pixM / (NIGHT_FINE_TEXEL_M / NIGHT_SUB) : max(duv.x * NIGHT_FINE.x, duv.y * NIGHT_FINE.y) * NIGHT_SUB;
  float magnify = texelM / max(pixM, 1e-3);
  n.close = smoothstep(0.6, 1.5, magnify);
  n.light = map8;
  n.coarse = coarse8;
  n.page = 0.0;
  n.level = nightLuma(map8);
  n.display = sqrt(clamp(n.level / 0.7, 0.0, 1.0));
  n.floor = 1.0;
  n.haze = 1.0;
  if (n.close > 0.0 && !skip) {
    // How much of the floor and the 8K's limits apply (above).
    float fw = (1.0 - smoothstep(1500.0, 3000.0, pixShort)) * (1.0 - uNightLegacy);
    float keep8 = 1.0 - fw * smoothstep(4.0, 12.0, magnify);
    vec3 base = map8 * keep8;
    float l = n.level * keep8;
    float display = mix(n.display, nightPageDisplay(n.level), fw);
    n.coarse = mix(coarse8, nightLightsOnly(coarse8) * keep8, fw);
    float cells;
    vec4 s = nightImagerySampleGrad(uv, vec3(0.0), pageDx, pageDy, cells);
    if (s.a > 1e-4) {
      float pageM = NIGHT_PAGE_TEXEL_M * cells / s.a;
      n.page = s.a * smoothstep(1.0, 2.0, texelM / pageM) * n.close;
      float d = max(0.0, (s.r / s.a * 255.0 - NIGHT_OFFSET) / (255.0 - NIGHT_OFFSET));
      float lr = nightSrgbToLinear(d) * NIGHT_GAIN;
      float cap = mix(1.0, min(1.0, lr / max(l, 1e-6)), fw);
      base *= cap;
      l *= cap;
      display = mix(display, min(display, d), fw);
      display = mix(display, d, n.page);
      // The Black Marble clips every city's middle to its top value; a
      // shoulder brings those wide flat areas under the bloom's threshold,
      // the structure's lights still rising past it.
      float lp = NIGHT_SHOULDER * (1.0 - exp(-lr / NIGHT_SHOULDER));
      l = mix(l, lp, n.page);
      // About 4 km wide: eight finest texels' worth of footprint.
      float coarseCells;
      vec4 c = nightImagerySampleGrad(uv, vec3(0.0), pageDx * 8.0 + vec2(8.0 / ${f(FINE_PER_PAGE)}, 0.0), pageDy * 8.0 + vec2(0.0, 8.0 / ${f(FINE_PER_PAGE)}), coarseCells);
      if (c.a > 1e-4) {
        float cl = nightSrgbToLinear(max(0.0, (c.r / c.a * 255.0 - NIGHT_OFFSET) / (255.0 - NIGHT_OFFSET))) * NIGHT_GAIN;
        cl = NIGHT_SHOULDER * (1.0 - exp(-cl / NIGHT_SHOULDER));
        n.coarse = mix(n.coarse, cl * nightLightTint(cl, mix(0.08, 0.3, ledShare)), n.page);
      }
    }
    float fl = mix(1.0, nightFloor(display), fw);
    // The structure under the data (the 8K's blobs too, until the pages
    // land), and each light coloured by its own brightness. Most of a night
    // view is dark ground with nothing to structure, so it is skipped there
    // (no derivatives below this point, so the branch is safe).
    if (l * fl > 1e-5) {
      // The region's LED share is a lean, from a city of mostly sodium to
      // one of a third LED; no region is all one or the other.
      float led = mix(0.08, 0.3, ledShare);
      // The metros' cores: where the 8K about 28 km wide is near its top
      // (central London, Paris and Los Angeles read 0.95, their suburbs and
      // the Bay Area's shores 0.5 to 0.7).
      float core = smoothstep(0.65, 0.92, wide8);
      vec2 st = nightDetail(q, foot, display, core, led, cos(lat));
      // The structure is the land's: out on open sea the data's lights are
      // left as they are (their mean, which the structure keeps too). By the
      // shore it runs on to the coast, where the light goes dark below, so
      // the towns end at the water instead of in an even band of their mean.
      st.x = mix(st.x, 1.0, water.x * water.y);
      // A light's colour follows its neighbourhood's brightness more than its
      // own peak: sodium stays orange however bright the lamp, and the tone
      // map already takes the brightest lines to yellow-white.
      n.light = mix(base * st.x, l * st.x * nightLightTint(l * mix(1.0, st.x, 0.15), st.y), max(n.page, n.close * 0.7)) * fl;
    } else {
      n.light = base * (1.0 - max(n.page, n.close * 0.7)) * fl;
    }
    // Close in, the shore's spill goes dark on the sea; open sea keeps its
    // lights.
    n.light *= 1.0 - water.x * (1.0 - water.y) * n.close;
    n.light *= uNightExposure;
    n.coarse *= uNightExposure;
    n.level = l;
    n.display = display;
    n.floor = fl;
    // The glow round bright cores (about 4 km wide) is how a city reads from
    // orbit. Once its streets are drawn it is a haze laid over the whole
    // metro, which held the dark between the streets at a fifth of the
    // lights, where the crew's photographs are black there. So it goes as
    // the streets come in, to a fifth by a pixel of 60 m.
    n.haze = mix(1.0, 0.2 + 0.8 * smoothstep(0.5, 3.3, foot), fw);
  }
  return n;
}

// The lights about 4 km wide at uv (linear, coloured), for the glow on the
// cloud bases over them: the pages' coarse read where they are resident, the
// 8K's (texel its size) a mip coarser where not. Close in (as nightLightsFrom
// has it) the 8K's moonlit land is taken out, and the glow is only over
// ground the data lights (its floor at the point): a cloud takes a town's
// light where it is over the town, and the coarse read alone carried it
// kilometres out over dark ground, a lit rim round every town wherever there
// was cloud. Takes screen derivatives.
vec3 nightGlowAt(sampler2D map, vec2 uv, vec2 texel, float ledShare) {
  vec2 duv = dFdx(uv), duv2 = dFdy(uv);
  vec3 g = textureGrad(map, uv, duv + vec2(texel.x, 0.0), duv2 + vec2(0.0, texel.y)).rgb;
  vec3 under8 = textureGrad(map, uv, duv, duv2).rgb;
  vec2 axes = nightPixelAxes(duv, duv2, (uv.y - 0.5) * 3.14159265);
  float fw = (1.0 - smoothstep(1500.0, 3000.0, min(axes.x, axes.y))) * (1.0 - uNightLegacy);
  g = mix(g, nightLightsOnly(g), fw);
  float under = nightPageDisplay(nightLuma(under8));
  vec2 raw = vec2(uv.x * ${f(night.PAGE_WIDTH)}, (1.0 - uv.y) * ${f(night.PAGE_HEIGHT)});
  vec2 rawDx = dFdx(raw), rawDy = dFdy(raw);
  float cells;
  vec4 c = nightImagerySampleGrad(uv, vec3(0.0), rawDx * 8.0 + vec2(8.0 / ${f(FINE_PER_PAGE)}, 0.0), rawDy * 8.0 + vec2(0.0, 8.0 / ${f(FINE_PER_PAGE)}), cells);
  if (c.a > 1e-4) {
    float cl = nightSrgbToLinear(max(0.0, (c.r / c.a * 255.0 - NIGHT_OFFSET) / (255.0 - NIGHT_OFFSET))) * NIGHT_GAIN;
    cl = NIGHT_SHOULDER * (1.0 - exp(-cl / NIGHT_SHOULDER));
    g = mix(g, cl * nightLightTint(cl, mix(0.08, 0.3, ledShare)), c.a);
  }
  if (fw > 0.0) {
    float fineCells;
    vec4 s = nightImagerySampleGrad(uv, vec3(0.0), rawDx, rawDy, fineCells);
    if (s.a > 1e-4) under = mix(under, max(0.0, (s.r / s.a * 255.0 - NIGHT_OFFSET) / (255.0 - NIGHT_OFFSET)), s.a);
    g *= mix(1.0, nightFloor(under), fw);
  }
  return g * uNightExposure;
}

// The globe's lights at uv (the Earth's equirectangular address, as the day
// map is read) from the 8K map (texel its size), with the sea from
// nightWater.
NightLights earthNightLights(sampler2D map, vec2 uv, vec2 texel, float ledShare, bool skip, vec2 water) {
  vec2 duv = max(abs(dFdx(uv)), abs(dFdy(uv)));
  float lat = (uv.y - 0.5) * 3.14159265;
  float pixM = max(duv.x * 6.2831853 * NIGHT_R * max(cos(lat), 0.02), duv.y * 3.14159265 * NIGHT_R);
  float texelM = texel.y * 3.14159265 * NIGHT_R;
  vec3 map8 = nightMapRead(map, uv, texel, texelM / max(pixM, 1e-3));
  float wide8 = nightLuma(textureLod(map, uv, 2.5).rgb);
  return nightLightsFrom(map8, texture2D(map, uv).rgb, wide8, texelM, uv, ledShare, skip, water);
}

// Whether uv lies in the cap an Earth patch covers (uNightSkipCap).
bool nightUnderPatch(vec2 uv) {
  float lat = (uv.y - 0.5) * 3.14159265, lon = (uv.x - 0.5) * 6.2831853;
  return dot(vec3(cos(lat) * cos(lon), sin(lat), -cos(lat) * sin(lon)), uNightSkipCap.xyz) > uNightSkipCap.w;
}

// Ground-level lights, for the Earth patches from under 30 km up.
// nightDetail is drawn for the view from above (its streets and lamps at
// least a pixel wide, in q from the globe's uv, a metre or two of float error
// at this scale), so from a few kilometres up the patches draw this instead:
// every light is a lamp at its true size and spacing, on streets, lots and
// yards, and the data says how many of them are lit, so a dim fringe is a
// few lit streets at a lamp's own brightness.
//
// q is in nightDetail's sub-cells (122 m) local to the patch and base the
// integer cell of its origin, so the hash is the world's and the maths stays
// in small numbers (the globe's uv carries a metre or two of float error
// here). P is the pixel's footprint as a covariance in q's units (its two
// screen axes), so a lamp under a pixel is a pixel across along both screen
// axes, a grazing view included; foot is the footprint's long axis, for the
// handoffs to each layer's mean, so nothing aliases.

// (q, base) at 1 / k the scale, for coarser grids on the same world lattice.
void nightCoarser(vec2 q, ivec2 base, int k, out vec2 qk, out ivec2 bk) {
  bk = base / k;
  qk = (q + vec2(base - bk * k)) / float(k);
}

float nightValueNoiseAt(vec2 q, ivec2 base, uint seed) {
  vec2 i = floor(q), f = q - i, u = f * f * (3.0 - 2.0 * f);
  ivec2 c = ivec2(i) + base;
  float a = nightRand(c, seed), b = nightRand(c + ivec2(1, 0), seed);
  float d = nightRand(c + ivec2(0, 1), seed), e = nightRand(c + ivec2(1, 1), seed);
  return mix(mix(a, b, u.x), mix(d, e, u.x), u.y);
}

// A gaussian of covariance S at offset r, of unit integral.
float nightGauss(vec2 r, mat2 S) {
  return exp(-0.5 * dot(r, inverse(S) * r)) / (6.2831853 * sqrt(max(determinant(S), 1e-20)));
}

// A lamp (a third of a metre across, so always a point) at offset r in a
// street's frame (x across, y along, the frame's axes ax and al in q), and
// the pool under it (pool.x across, pool.y along) with poolShare of its light.
float nightLamp(vec2 r, vec2 ax, vec2 al, mat2 P, vec2 pool, float poolShare) {
  mat2 R = mat2(ax, al);
  mat2 Pl = transpose(R) * P * R;
  float point = nightGauss(r, Pl + mat2(6.25e-6, 0.0, 0.0, 6.25e-6));
  float pl = nightGauss(r, Pl + mat2(pool.x * pool.x, 0.0, 0.0, pool.y * pool.y));
  return mix(point, pl, poolShare);
}

// A street network (the Voronoi edges of q's grid) with a
// lamp every sp cells along each lit street and a pool on the road under it
// taking poolShare of its light: from above a street lamp is mostly its
// lens, the pool a few percent, which is what keeps a lit street a chain of
// lamps and not a glowing vein. pOn is the share of streets lit, each at a
// lamp's full brightness. Lamps hand to the lit line once they come under
// about three pixels apart, the lines to their mean once the blocks do. x:
// light averaging 1 over a few cells; y: its LED share; z: the distance to
// the street (cells), for what stands back from it. block: the cell whose
// block q is in.
vec3 nightLampNet(vec2 q, ivec2 base, float foot, mat2 P, float sp, vec2 pool, float poolShare, float pOn, float ledShare, uint seed, out ivec2 block) {
  vec2 cell = floor(q);
  float d1 = 1e9, d2 = 1e9;
  vec2 p1 = vec2(0.0), p2 = vec2(0.0);
  ivec2 c1 = ivec2(0), c2 = ivec2(0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    ivec2 lc = ivec2(cell) + ivec2(i, j);
    ivec2 c = lc + base;
    vec2 p = vec2(lc) + vec2(nightRand(c, seed + 3u), nightRand(c, seed + 5u)) * 0.8 + 0.1;
    float d = dot(q - p, q - p);
    if (d < d1) { d2 = d1; p2 = p1; c2 = c1; d1 = d; p1 = p; c1 = c; }
    else if (d < d2) { d2 = d; p2 = p; c2 = c; }
  }
  // The street between two cells, oriented the same from either side so its
  // lamps stand in the same places.
  bool swap = c2.x < c1.x || (c2.x == c1.x && c2.y < c1.y);
  ivec2 lo = swap ? c2 : c1, hi = swap ? c1 : c2;
  vec2 plo = swap ? p2 : p1, phi = swap ? p1 : p2;
  vec2 n = normalize(phi - plo), tn = vec2(-n.y, n.x);
  vec2 m = 0.5 * (plo + phi);
  float e = dot(q - m, n);
  float t = dot(q - m, tn);
  ivec2 edge = lo * 5 + (hi - lo) + 2;
  block = c1;
  float on = step(nightRand(edge, seed + 53u), pOn);
  float level = 0.6 + 0.8 * nightRand(edge, seed + 59u);
  float led = step(1.0 - ledShare, nightRand(edge, seed + 61u));
  float ph = nightRand(edge, seed + 67u) * sp;
  float dt = t - ph - sp * floor((t - ph) / sp + 0.5);
  // The edges run 1.86 cells of length per cell of area, so a lamp every sp
  // is sp / 1.86 of a cell's area each.
  float lamp = (sp / 1.86) * nightLamp(vec2(e, dt), n, tn, P, pool, poolShare);
  float lw = pool.x * pool.x + dot(n, P * n);
  float line = exp(-0.5 * e * e / lw) / (1.86 * 2.5066283 * sqrt(lw));
  float v = mix(line, lamp, 1.0 - smoothstep(0.25 * sp, 0.5 * sp, foot));
  float k = 1.0 - smoothstep(0.25, 0.5, foot);
  return vec3(mix(1.0, on * level * v / pOn, k), led, abs(e));
}

// A planned town's streets on q's grid: a line along each axis about every
// cell, each shifted by its own amount and lit in runs about three cells
// long, with a lamp every 1 / n cells along it (n whole, so the lamps keep
// their places whatever the patch's origin) and a pool under each. As
// nightLampNet otherwise.
vec3 nightLampGridNet(vec2 q, ivec2 base, float foot, mat2 P, float n, vec2 pool, float poolShare, float pOn, float ledShare, uint seed) {
  float kl = 1.0 - smoothstep(0.25 / n, 0.5 / n, foot);
  float acc = 0.0, accLed = 0.0, dmin = 1e9;
  for (int axis = 0; axis < 2; axis++) {
    float across = axis == 0 ? q.y : q.x;
    float along = axis == 0 ? q.x : q.y;
    vec2 ax = axis == 0 ? vec2(0.0, 1.0) : vec2(1.0, 0.0);
    vec2 al = axis == 0 ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    int baseA = axis == 0 ? base.y : base.x;
    int baseB = axis == 0 ? base.x : base.y;
    float lw = pool.x * pool.x + dot(ax, P * ax);
    float kf = floor(across);
    // Lines move at most 0.3 of a cell, so only the two either side matter.
    for (int dk = 0; dk <= 1; dk++) {
      int kk = int(kf) + dk;
      int kg = kk + baseA;
      float e = across - (float(kk) + (nightRand(ivec2(kg, axis), seed) - 0.5) * 0.6);
      dmin = min(dmin, abs(e));
      int run = int(floor((along + float(baseB)) / 3.0 + nightRand(ivec2(kg, axis + 2), seed + 1u)));
      ivec2 id = ivec2(kg * 2 + axis, run);
      float on = step(nightRand(id, seed + 53u), pOn);
      float level = 0.6 + 0.8 * nightRand(id, seed + 59u);
      float led = step(1.0 - ledShare, nightRand(id, seed + 61u));
      float dt = (fract(along * n + nightRand(ivec2(kg, axis + 4), seed + 67u)) - 0.5) / n;
      // Two cells of street per cell of area, so each lamp has 1 / (2n).
      float lamp = nightLamp(vec2(e, dt), ax, al, P, pool, poolShare) / (2.0 * n);
      float line = exp(-0.5 * e * e / lw) / (2.0 * 2.5066283 * sqrt(lw));
      float v = on * level * mix(line, lamp, kl);
      acc += v;
      accLed += v * led;
    }
  }
  float k = 1.0 - smoothstep(0.25, 0.5, foot);
  return vec3(mix(1.0, acc / pOn, k), acc > 1e-6 ? accLed / acc : ledShare, dmin);
}

// Point lights on a jittered grid of cell g (q's units, 1 / g a whole
// number), each on with chance pOn; mean 1 over a few cells. x: the light,
// y: its LED share.
vec2 nightLampGrid(vec2 q, ivec2 base, float foot, mat2 P, float g, float pOn, float ledShare, uint seed) {
  vec2 qg = q / g;
  mat2 S = P / (g * g) + mat2(6.25e-6 / (g * g), 0.0, 0.0, 6.25e-6 / (g * g));
  vec2 acc = vec2(0.0);
  // Points sit 0.2 to 0.8 into their cells, so the two cells either side of
  // q on each axis hold every one near it.
  for (int j = 0; j <= 1; j++) for (int i = 0; i <= 1; i++) {
    vec2 lc = floor(qg - 0.5) + vec2(i, j);
    ivec2 c = ivec2(lc) + base * int(1.0 / g + 0.5);
    vec2 at = lc + vec2(nightRand(c, seed + 11u), nightRand(c, seed + 23u)) * 0.6 + 0.2;
    float on = step(nightRand(c, seed + 37u), pOn);
    float b = 0.5 + nightRand(c, seed + 41u);
    float led = step(1.0 - ledShare, nightRand(c, seed + 43u));
    float pt = on * b * nightGauss(qg - at, S);
    acc += vec2(pt, pt * led);
  }
  float k = 1.0 - smoothstep(0.25, 0.5, foot * 0.42 / g);
  vec2 v = acc / max(pOn, 1e-3);
  return mix(vec2(1.0, ledShare), vec2(v.x, v.y / max(v.x, 1e-5)), k);
}

// The ground's lights at q: S averaging 1 (times the data's light gives the
// radiance) and its LED share. d is the lit share, the data's light over a
// fully lit core's, so a dim fringe has few lit streets, lots and yards,
// each as bright as a core's.
vec2 nightGroundLamps(vec2 q, ivec2 base, float foot, mat2 P, float d, float ledShare) {
  float dd = clamp(d, 0.0, 1.0);
  // Planned grids in some regions (most of the United States), old
  // irregular towns in others, mixed over a couple of hundred kilometres as
  // nightDetail's roads are.
  vec2 qg; ivec2 bg;
  nightCoarser(q, base, 2600, qg, bg);
  float gridShare = smoothstep(0.35, 0.65, nightValueNoiseAt(qg, bg, 151u));
  // Neighbourhood streets: blocks of one sub-cell (122 m), a lamp every
  // 60 m, pools 4 m across and 8 m along.
  float pSt = clamp(0.85 * dd, 0.01, 0.85);
  ivec2 block = ivec2(floor(q)) + base, unused;
  vec3 st = vec3(0.0);
  if (gridShare < 1.0) st += (1.0 - gridShare) * nightLampNet(q, base, foot, P, 0.5, vec2(0.03, 0.065), 0.03, pSt, ledShare, 211u, block);
  if (gridShare > 0.0) st += gridShare * nightLampGridNet(q, base, foot, P, 2.0, vec2(0.03, 0.065), 0.03, pSt, ledShare, 213u);
  // Arterials, a lamp every 40 m with wider pools: a network nine times
  // coarser (1.1 km), or the section-line grid of a mile (13 cells). Only
  // some of them lit, even in a core: the whole mesh lit read as cracked mud.
  float pAr = clamp(0.1 + 0.55 * dd, 0.05, 0.65);
  vec3 ar = vec3(0.0);
  if (gridShare < 1.0) {
    vec2 q9; ivec2 b9;
    nightCoarser(q, base, 9, q9, b9);
    ar += (1.0 - gridShare) * nightLampNet(q9, b9, foot / 9.0, P / 81.0, 0.037, vec2(0.005, 0.009), 0.05, pAr, ledShare, 223u, unused);
  }
  if (gridShare > 0.0) {
    vec2 q13; ivec2 b13;
    nightCoarser(q, base, 13, q13, b13);
    ar += gridShare * nightLampGridNet(q13, b13, foot / 13.0, P / 169.0, 39.0, vec2(0.004, 0.007), 0.05, pAr, ledShare, 227u);
  }
  // Lots: some blocks (more in the cores) lit wall to wall by a grid of
  // white lamps 24 m apart, set back from the street.
  float lotShare = 0.01 + 0.10 * smoothstep(0.3, 1.0, dd);
  float isLot = step(nightRand(block, 229u), lotShare);
  float setBack = smoothstep(0.05, 0.09, st.z);
  vec2 lot = nightLampGrid(q, base, foot, P, 0.2, 0.9, 0.6, 233u);
  float lots = isLot * setBack * lot.x / (lotShare * 0.7);
  // Yards and windows: dim scattered points between the streets.
  vec2 yard = nightLampGrid(q, base, foot, P, 0.25, clamp(0.35 * dd, 0.01, 0.35), ledShare, 239u);
  float yards = setBack * yard.x / 0.7;
  float S = 0.6 * st.x + 0.15 * ar.x + 0.13 * lots + 0.12 * yards;
  float led = (0.6 * st.x * st.y + 0.15 * ar.x * ar.y + 0.13 * lots * 0.6 + 0.12 * yards * yard.y) / max(S, 1e-4);
  // Neighbourhoods brighter and darker over a few hundred metres, soft, mean 1.
  vec2 q5; ivec2 b5;
  nightCoarser(q, base, 5, q5, b5);
  S *= 0.45 + 1.1 * (0.65 * nightValueNoiseAt(q5, b5, 241u) + 0.35 * nightValueNoiseAt(q5 * 2.0, b5 * 2, 251u));
  return vec2(S, led);
}

// The tint the globe gives a region's lights once single towns are drawn
// (earth.js cityTintNear), luminance about 0.82: a slight lean toward warm or
// cool, the lights' own tints being in the structure.
vec3 nightTintNear(float ledShare) {
  return mix(vec3(1.0, 0.95, 0.90) / 0.9570, vec3(0.97, 0.98, 1.0) / 0.9793, ledShare) * 0.82;
}
`;

// The patches' share of the globe's state: the Sun's direction in the
// Earth's surface-fixed frame (they are lit by the Moon at night, so their
// key light cannot say how far below the horizon the Sun is) and the globe's
// 8K night map. Set each frame by createGlobalNightImagery.
export const nightSunUniforms = { uNightSunSurface: { value: new THREE.Vector3(1, 0, 0) } };
// The 8K, also for the light the volumetric clouds take from below
// (volclouds.js), in the viewer as in rocketry.
export const nightMapUniforms = { uNightMap8: { value: null } };
const BLACK = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
BLACK.needsUpdate = true;
// The globe's 8K night map, or black while it has not loaded: until then the
// globe's nightTexture is its day map (main.js), and every land texel of
// that would read as a city.
function nightMapOf(u) {
  const t = u?.nightTexture?.value;
  return t && t !== u.dayTexture?.value ? t : BLACK;
}
// The shared before-and-after switch (uNightLegacy), each frame.
function readNightLegacy() {
  night.uniforms.uNightLegacy.value = globalThis.__rkNightLampsOff ? 1 : 0;
}

const PATCH_VERT = /* glsl */`
#include <common>
#include <fog_pars_vertex>
#include <logdepthbuf_pars_vertex>
attribute vec2 geo;
attribute float rim;
varying vec2 vGeo;
varying float vRim;
varying vec3 vViewPosition;
varying vec2 vPatchXZ;
void main() {
  vGeo = geo;
  vPatchXZ = position.xz;
  vRim = rim;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vViewPosition = -mvPosition.xyz;
  gl_Position = projectionMatrix * mvPosition;
  #include <logdepthbuf_vertex>
  #include <fog_vertex>
}
`;
// The globe's close-in composition (earth.js: the lights at 2.5 by the near
// tint, at 0.6, with the glow round bright cores) over the globe's twilight
// interval, through the same air the ground sees: environment.js's fog
// chunk's optical depth (its HAZE_H, its curved path past 30 km), the
// patch's up from uUpView. The chunk reads up from the scene's hemisphere
// light, which needs lights: true, and a material with that took the site's
// spotlights off the pad, so this one has no lights at all.
const PATCH_FRAG = /* glsl */`
#include <common>
#include <fog_pars_fragment>
#include <logdepthbuf_pars_fragment>
uniform vec3 uUpView;
varying vec2 vGeo;
varying float vRim;
varying vec3 vViewPosition;
// Metres from the patch's centre, which on the Space Center's patch
// (uSite 1) is the pad.
varying vec2 vPatchXZ;
uniform float uSite;
uniform vec2 uImageryOrigin;
uniform vec3 uNightSunSurface;
uniform sampler2D uNightMap8;
// The water mask the ground's pages keep off the sea (earthimagery.js).
uniform sampler2D uEarthOceanMask;
// The ground lamps' lattice: q's whole cell at the patch's origin (exact in
// a float) and the fraction left over (nightPatchBeforeRender).
uniform vec2 uNightQCell;
uniform vec2 uNightQFrac;
// The camera's height above the Earth (m), from nightPatchBeforeRender.
uniform float uNightCamAlt;
// 1 draws the lights as they were before the lamps and the floor
// (window.__rkNightLampsOff, for before and after captures).
uniform float uNightLampsOff;
// The ground's own coastline (coast.js, as terrain.js reads it), so lamps end
// at the shore the ground draws; uNightCoastOn 0 where the patch has none.
uniform float uNightCoastOn;
uniform float uCoastOn;
uniform highp sampler2DArray uCoastMasks;
uniform vec3 uCoastRange;
uniform vec3 uCoastBorder;
uniform vec4 uXfNear;
uniform vec4 uXfFar;
uniform vec4 uXfRegion;
${NIGHT_IMAGERY_GLSL}
float nCoastBoxW(vec2 uv, float b) { vec2 m = min(uv, 1.0 - uv); return smoothstep(0.0, b, min(m.x, m.y)); }
vec2 nCoastDecode(vec2 c, float range) { vec2 s = (c * 255.0 - 128.0) / 127.0; return sign(s) * s * s * range; }
// Metres to the nearest water, land positive, as the ground has it (x), and
// how much of that came from the coast masks (y; the region's box holds the
// others), where the globe's 4.9 km water mask is all there is otherwise.
vec2 nLandDistance(vec2 uv) {
  float d = (0.5 - texture2D(uEarthOceanMask, uv).r) * 8000.0;
  if (uNightCoastOn < 0.5 || uCoastOn < 0.5) return vec2(d, 0.0);
  vec2 uvN = vGeo * uXfNear.xy + uXfNear.zw;
  vec2 uvF = vGeo * uXfFar.xy + uXfFar.zw;
  vec2 uvR = vGeo * uXfRegion.xy + uXfRegion.zw;
  float wR = nCoastBoxW(uvR, uCoastBorder.z);
  d = mix(d, nCoastDecode(textureLod(uCoastMasks, vec3(uvR.x, 1.0 - uvR.y, 2.0), 0.0).rg, uCoastRange.z).x, wR);
  d = mix(d, nCoastDecode(textureLod(uCoastMasks, vec3(uvF.x, 1.0 - uvF.y, 1.0), 0.0).rg, uCoastRange.y).x, nCoastBoxW(uvF, uCoastBorder.y));
  d = mix(d, nCoastDecode(textureLod(uCoastMasks, vec3(uvN.x, 1.0 - uvN.y, 0.0), 0.0).rg, uCoastRange.x).x, nCoastBoxW(uvN, uCoastBorder.x));
  return vec2(d, wR);
}
void main() {
  #include <logdepthbuf_fragment>
  vec2 nUV = (vGeo + uImageryOrigin) / vec2(360.0, 180.0) + 0.5;
  float nLat = (nUV.y - 0.5) * 3.14159265, nLon = (nUV.x - 0.5) * 6.2831853;
  vec3 nN = vec3(cos(nLat) * cos(nLon), sin(nLat), -cos(nLat) * sin(nLon));
  float nNight = 1.0 - smoothstep(-0.10, 0.22, dot(nN, uNightSunSurface));
  float nLed = nightLedShare(nUV);
  vec2 nWater = nightWater(texture2D(uEarthOceanMask, nUV).r, textureLod(uEarthOceanMask, nUV, 2.5).r);
  NightLights nl = earthNightLights(uNightMap8, nUV, 1.0 / vec2(textureSize(uNightMap8, 0)), nLed, false, nWater);
  vec3 nTint = nightTintNear(nLed);
  // The glow round bright cores, only over ground the data lights (as the
  // globe's).
  vec3 nGlow = nl.coarse * 2.5 * nTint * smoothstep(0.04, 0.4, nightLuma(nl.coarse)) * 0.14 * nl.floor * nl.haze;
  // As the globe's: not on the sea by the shore once close.
  nGlow *= 1.0 - nWater.x * (1.0 - nWater.y) * nl.close;
  // Ground metres a pixel spans (its long axis), from the view-space
  // position: the uv's derivatives are a metre or two of float noise here.
  float nPixM = max(length(dFdx(vViewPosition)), length(dFdy(vViewPosition)));
  // The ground's own coastline where the coast masks reach. The data's
  // 500 m texels carry a town's light a kilometre or two out over the
  // lagoons and lakes beside it, which the globe's water mask is too coarse
  // to see, and the floor cannot keep that dark: within 1.5 km of the
  // Indian River's shore the spill is over 0.08 on almost half the texels
  // and over 0.2 on a fifth. So no light leaves the land: the pixel's share
  // of land, from the distance to the water across its footprint.
  vec2 nLandD = nLandDistance(nUV);
  float nLandCover = mix(1.0, clamp(0.5 + nLandD.x / max(nPixM, 1.0), 0.0, 1.0), nLandD.y * (1.0 - uNightLampsOff));
  // The pixel's footprint in the lamps' q (x east, y south; the patch's
  // frame is east and south at its centre), each screen axis a column.
  float nLatR = radians(uImageryOrigin.y + vGeo.y);
  vec2 nKq = vec2(${f(Q_PER_DEG[0])} / (111320.0 * max(cos(nLatR), 0.05)), ${f(-Q_PER_DEG[1])} / 110574.0);
  vec2 nAx = dFdx(vPatchXZ) * nKq, nAy = dFdy(vPatchXZ) * nKq;
  // The data's floor (nightLightsFrom puts it in nl.light already; the
  // lamps below take their count from the data under it).
  float nFloor = nightFloor(nl.display);
  vec3 nLight = nl.light * nLandCover;
  nGlow *= nLandCover;
  // From under 30 km up, the lamps themselves, handing to the orbit's
  // structure by 120 km. By the camera's height and not the pixel's
  // footprint: toward the horizon the footprint is kilometres deep however
  // low the camera, and the orbit's streets are drawn for orbit. The lamps
  // hand to their own means by the footprint (its long axis, so nothing
  // aliases).
  float nGround = (1.0 - smoothstep(30000.0, 120000.0, uNightCamAlt)) * (1.0 - uNightLampsOff);
  float nLand = smoothstep(5.0, 25.0, nLandD.x);
  if (nGround > 0.0) {
    float lv = nl.level * nFloor;
    vec3 nGrd = vec3(0.0);
    if (lv > 1e-4 && nLand > 0.0) {
      vec2 q = vGeo * vec2(${f(Q_PER_DEG[0])}, ${f(Q_PER_DEG[1])}) + uNightQFrac;
      // 0.42 of a pixel's width, as nightDetail widens its lights.
      mat2 P = 0.1764 * (outerProduct(nAx, nAx) + outerProduct(nAy, nAy));
      float foot = max(length(nAx), length(nAy));
      // A fully lit core is the shouldered top of the data (0.565).
      vec2 g = nightGroundLamps(q, ivec2(uNightQCell), foot, P, lv / 0.565, mix(0.08, 0.3, nLed));
      // A lamp is a point a pixel wide at most a few times a core's light,
      // so its bloom is a glint, not a disc.
      float L = min(lv * g.x, 1.6);
      nGrd = L * nightLightTint(lv * mix(1.0, g.x, 0.35), g.y) * nLand * uNightExposure;
    }
    nLight = mix(nLight, nGrd, nGround);
    // The 4 km glow round the cores is how a town reads from orbit; from a
    // few kilometres up it laid a haze disc over the town.
    nGlow *= 1.0 - nGround;
  }
  // Emission is the right model where a pixel takes in many lamps, from
  // altitude or far off. Within a few kilometres of the eye a lamp lights a
  // pool of ground (the site draws its own), and the data's 500 m texels
  // would paint the ground under the pad as one lit sheet, so the lights
  // come in from 1.5 to 6 km away.
  float nNear = smoothstep(1500.0, 6000.0, length(vViewPosition));
  // The lamps are points, so they paint no sheet: under 30 km they come in
  // from 60 m from the eye (a flight over a town sees the streets below),
  // kept only off the launch complex, which lights itself.
  nNear = mix(nNear, smoothstep(60.0, 250.0, length(vViewPosition)) * mix(1.0, smoothstep(1200.0, 2500.0, length(vPatchXZ)), uSite), nGround);
  gl_FragColor = vec4((nLight * 2.5 * nTint * 0.6 + nGlow) * nNight * vRim * nNear, 1.0);
  #if defined( USE_FOG ) && !defined( FOG_EXP2 )
    vec3 rkRay = -vViewPosition;
    float rkLen = length(rkRay);
    float rkCamH = max(fogNear, 0.0);
    float rkBeta = fogFar * 1e-6;
    const float rkH = 2000.0;
    float rkDh = dot(rkRay, uUpView);
    float rkE0 = exp(-rkCamH / rkH), rkE1 = exp(-max(rkCamH + rkDh, 0.0) / rkH);
    float rkTau = abs(rkDh) > 1.0 ? rkBeta * rkLen * rkH * (rkE0 - rkE1) / rkDh : rkBeta * rkLen * rkE0;
    if (rkLen > 30000.0) rkTau = mix(rkTau, rkBeta * airCurvedDepth(rkCamH, rkDh / rkLen, rkLen, rkH), smoothstep(30000.0, 60000.0, rkLen));
    gl_FragColor.rgb *= exp(-max(rkTau, 0.0));
  #endif
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

// The patch's up in view space, for the lights' air path; set by
// nightPatchBeforeRender.
const nightUpView = { value: new THREE.Vector3(0, 1, 0) };
const _upW = new THREE.Vector3(), _camL = new THREE.Vector3();
// onBeforeRender for a lights mesh riding on a patch whose group is group
// (its local +y is up at the patch's centre). Also splits the ground lamps'
// lattice at the patch's origin into a whole cell and a fraction, in double
// precision, since the origin moves with a recentre.
export const nightPatchBeforeRender = (group) => (renderer, scene, camera, geometry, material) => {
  _upW.set(0, 1, 0).transformDirection(group.matrixWorld);
  nightUpView.value.copy(_upW).transformDirection(camera.matrixWorldInverse);
  const u = material?.uniforms;
  if (u?.uNightLampsOff) u.uNightLampsOff.value = globalThis.__rkNightLampsOff ? 1 : 0;
  if (u?.uNightCamAlt) {
    // The patch's frame has its origin on the ground at its centre, +y up.
    _camL.setFromMatrixPosition(camera.matrixWorld);
    group.worldToLocal(_camL);
    u.uNightCamAlt.value = Math.hypot(_camL.x, EARTH_R + _camL.y, _camL.z) - EARTH_R;
  }
  if (u?.uNightQCell) {
    const o = u.uImageryOrigin.value;
    const qx = Q_ZERO[0] + o.x * Q_PER_DEG[0], qy = Q_ZERO[1] + o.y * Q_PER_DEG[1];
    const cx = Math.floor(qx), cy = Math.floor(qy);
    u.uNightQCell.value.set(cx, cy);
    u.uNightQFrac.value.set(qx - cx, qy - cy);
  }
};

// The lights pass for one ring of an Earth patch: its geometry again, added
// over the ground after it is drawn (terrain.js). imageryOrigin is the
// ground material's own uniform, so a recentre moves both; ground is the
// ground material's uniforms, for its coastline transforms (the same
// objects, so a recentre moves those too).
export function makeNightPatchMaterial(imageryOrigin, { transparent = false, ground = null } = {}) {
  const coast = ground?.uXfNear && ground.uXfFar && ground.uXfRegion;
  return new THREE.ShaderMaterial({
    uniforms: {
      ...THREE.UniformsUtils.merge([THREE.UniformsLib.fog]),
      uUpView: nightUpView,
      ...nightImageryUniforms,
      ...nightSunUniforms,
      ...nightMapUniforms,
      uEarthOceanMask: earthImageryUniforms.uEarthOceanMask,
      uImageryOrigin: imageryOrigin,
      uNightQCell: { value: new THREE.Vector2() },
      uNightQFrac: { value: new THREE.Vector2() },
      uNightCamAlt: { value: 0 },
      uNightLampsOff: { value: 0 },
      uNightCoastOn: { value: coast ? 1 : 0 },
      uSite: ground?.uSite || { value: 0 },
      uCoastOn: coastUniforms.uCoastOn,
      uCoastMasks: coastUniforms.uCoastMasks,
      uCoastRange: coastUniforms.uCoastRange,
      uCoastBorder: coastUniforms.uCoastBorder,
      uXfNear: coast ? ground.uXfNear : { value: new THREE.Vector4() },
      uXfFar: coast ? ground.uXfFar : { value: new THREE.Vector4() },
      uXfRegion: coast ? ground.uXfRegion : { value: new THREE.Vector4() }
    },
    vertexShader: PATCH_VERT,
    fragmentShader: PATCH_FRAG,
    fog: true,
    blending: THREE.AdditiveBlending,
    // Over the transparent outer ring it has to come after the ring, so it
    // goes in the transparent list too; over the opaque rings the opaque
    // list's render order already draws it second.
    transparent,
    depthWrite: false,
    depthFunc: THREE.LessEqualDepth,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -2
  });
}

// One selection and upload step for the camera at position (metres, Earth's
// surface-fixed frame), looking along orientation through lens. sunDir (unit,
// the same frame) skips the update when nothing in view is dark.
export function updateNightImagery(renderer, { now, position, orientation, lens, sunDir = null }) {
  const stream = night.getStream(renderer);
  const r = position.length();
  const altitude = r - EARTH_R;
  if (altitude > STREAM_ALTITUDE) {
    // A phone frees the atlas well away from the Earth (as earthclose.js).
    if (RUNNING_ON_MOBILE && altitude > 8 * EARTH_R) stream.release();
    return stream;
  }
  if (sunDir) {
    // The view reaches the horizon, acos(R / r) from the point below; the
    // night side starts about 96 degrees from the Sun (the twilight the
    // shader fades the lights over). Skip while all of that is day.
    const horizon = Math.acos(Math.min(1, EARTH_R / r));
    const fromSun = Math.acos(THREE.MathUtils.clamp(position.dot(sunDir) / r, -1, 1));
    if (fromSun + horizon < 1.62) return stream;
  }
  const prefetch = 1 + (PREFETCH[0] - 1) * (1 - THREE.MathUtils.smoothstep(altitude, PREFETCH[1], PREFETCH[2]));
  stream.setEmptyTiles(!night.uniforms.uNightLegacy.value);
  stream.update({ now, position, orientation, lens, budget: BUDGET, prefetch });
  return stream;
}

// The normal viewer: the camera in the Earth mesh's frame, at either scale.
const _position = new THREE.Vector3(), _orientation = new THREE.Quaternion(), _sun = new THREE.Vector3(), _inv = new THREE.Matrix4();
export function updateViewerNightImagery(renderer, mesh, camera, now, sunWorld = null) {
  // No terrain patch in the viewer: the globe draws every light itself.
  night.uniforms.uNightSkipCap.value.w = 2;
  readNightLegacy();
  const u = mesh?.material?.uniforms;
  if (!u?.uNightAtlas || !mesh.geometry?.parameters?.radius) return null;
  nightMapUniforms.uNightMap8.value = nightMapOf(u);
  earthCameraFrame(mesh, camera, _position, _orientation);
  night.uniforms.uNightExposure.value = nightExposureAt(_position.length() - EARTH_R);
  let sunDir = null;
  if (sunWorld) {
    // The Sun's direction in the mesh's own (spinning) frame.
    _inv.copy(mesh.matrixWorld).invert();
    _sun.copy(sunWorld).applyMatrix4(_inv).normalize();
    sunDir = _sun;
  }
  return updateNightImagery(renderer, { now, position: _position, orientation: _orientation, lens: camera.projectionMatrix, sunDir });
}

// Rocketry: the flight camera over the true-scale Earth, while either night
// reader draws (the globe beyond the patch, or a patch).
export function createGlobalNightImagery(G) {
  const stream = getNightImageryStream(G.ctx.renderer);
  const position = new THREE.Vector3(), spin = new THREE.Quaternion(), orientation = new THREE.Quaternion();
  const sun = new THREE.Vector3();
  const off = G.events.on('frame', ({ now }) => {
    readNightLegacy();
    if (!G.R.active || G.R.viewBody !== 'earth' || G.R.mode === 'vab') { night.uniforms.uNightSkipCap.value.w = 2; return; }
    const p = G.bodies.inertialToSurface('earth', G.camInertial);
    position.set(p.x, p.y, p.z);
    night.uniforms.uNightExposure.value = nightExposureAt(position.length() - EARTH_R);
    G.bodies.spinQuaternion('earth', spin).invert();
    orientation.copy(spin).multiply(G.localCamera.quaternion);
    const si = G.bodies.inertialToSurface('earth', G.bodies.sunDirIn('earth', sun));
    sun.set(si.x, si.y, si.z);
    nightSunUniforms.uNightSunSurface.value.copy(sun);
    // The cap the visible Earth patch draws opaque: its fade starts 540 km
    // out (terrain.js rimFade), so 0.08 rad (510 km) is inside it.
    const roam = G.R.patches?.get('earth:roam'), rec = G.R.patches?.get('earth');
    const patch = roam && roam.patch.group.visible ? roam.patch : rec && rec.patch.group.visible ? rec.patch : null;
    const cap = night.uniforms.uNightSkipCap.value;
    if (patch) cap.set(patch.basis.up.x, patch.basis.up.y, patch.basis.up.z, Math.cos(0.08));
    else cap.w = 2;
    nightMapUniforms.uNightMap8.value = nightMapOf(G.bodies.get('earth').spinMesh?.material?.uniforms);
    updateNightImagery(G.ctx.renderer, { now, position, orientation, lens: G.localCamera.projectionMatrix, sunDir: sun });
  });
  G.nightImagery = stream;
  return { stats: stream.stats, dispose() { off(); night.uniforms.uNightSkipCap.value.w = 2; } };
}
